import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  /** Affichage compact pour les espaces restreints */
  compact?: boolean;
}

export function Pagination({ 
  currentPage, 
  totalPages, 
  totalItems,
  pageSize = 20,
  onPageChange,
  compact = false
}: PaginationProps) {
  // Générer les numéros de pages visibles (max 5 boutons)
  const getVisiblePages = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = compact ? 3 : 5;
    
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // Toujours montrer la première page
    pages.push(1);
    
    // Calculer la plage autour de la page actuelle
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    
    // Ajuster si on est près du début ou de la fin
    if (currentPage <= 2) {
      end = Math.min(totalPages - 1, compact ? 2 : 3);
    } else if (currentPage >= totalPages - 1) {
      start = Math.max(2, totalPages - (compact ? 1 : 2));
    }
    
    // Ajouter ellipsis si nécessaire avant
    if (start > 2) {
      pages.push('ellipsis');
    }
    
    // Ajouter les pages du milieu
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    // Ajouter ellipsis si nécessaire après
    if (end < totalPages - 1) {
      pages.push('ellipsis');
    }
    
    // Toujours montrer la dernière page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();
  
  // Calculer les infos de l'item range
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems || currentPage * pageSize);
  
  if (totalPages <= 1) return null;

  return (
    <div className={`flex items-center ${compact ? 'gap-1' : 'gap-2 justify-between'} ${compact ? '' : 'px-2 py-3'}`}>
      {/* Info items - seulement en mode non-compact */}
      {!compact && totalItems && (
        <div className="text-sm text-muted-foreground">
          <span className="hidden sm:inline">
            Affichage de{' '}
            <span className="font-medium text-foreground">{startItem}</span>
            {' '}à{' '}
            <span className="font-medium text-foreground">{endItem}</span>
            {' '}sur{' '}
            <span className="font-medium text-foreground">{totalItems}</span>
            {' '}résultats
          </span>
          <span className="sm:hidden text-xs">
            {startItem}-{endItem} / {totalItems}
          </span>
        </div>
      )}
      
      {/* Navigation */}
      <nav className="flex items-center gap-1">
        {/* Première page - seulement en mode non-compact */}
        {!compact && (
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Première page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
        
        {/* Page précédente */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Page précédente"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        {/* Numéros de page */}
        <div className="flex items-center gap-0.5">
          {visiblePages.map((page, index) => 
            page === 'ellipsis' ? (
              <span 
                key={`ellipsis-${index}`} 
                className="px-2 text-muted-foreground text-sm"
              >
                …
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`
                  min-w-[2rem] h-8 px-2 text-sm font-medium rounded-md transition-colors
                  ${currentPage === page
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                {page}
              </button>
            )
          )}
        </div>
        
        {/* Page suivante */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Page suivante"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        
        {/* Dernière page - seulement en mode non-compact */}
        {!compact && (
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Dernière page"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        )}
      </nav>
    </div>
  );
}